/** Called as the request body is sent, with the bytes sent so far and the total body size. */
export type UploadProgressCallback = (loaded: number, total: number) => void;

const nullBodyStatuses = new Set([204, 205, 304]);

function parseResponseHeaders(rawHeaders: string): Headers {
    const headers = new Headers();
    for (const line of rawHeaders.trim().split(/[\r\n]+/)) {
        const separatorIndex = line.indexOf(":");
        if (separatorIndex > 0) {
            headers.append(
                line.slice(0, separatorIndex).trim(),
                line.slice(separatorIndex + 1).trim(),
            );
        }
    }
    return headers;
}

/**
 * A `fetch` replacement that reports upload progress, which `fetch` cannot do. Resolves with a
 * standard `Response` so callers can keep their `fetch` response handling unchanged.
 */
export function xhrFetch(
    url: string,
    init: RequestInit & { signal: AbortSignal },
    onUploadProgress: UploadProgressCallback,
): Promise<Response> {
    return new Promise((resolve, reject) => {
        const { signal } = init;
        if (signal.aborted) {
            reject(new DOMException("The request was aborted.", "AbortError"));
            return;
        }

        const xhr = new XMLHttpRequest();
        xhr.open(init.method ?? "GET", url);
        // Same-origin XHR sends cookies by default, matching fetch's credentials: "same-origin"
        xhr.withCredentials = init.credentials === "include";
        xhr.responseType = "blob";
        for (const [name, value] of new Headers(init.headers).entries()) {
            xhr.setRequestHeader(name, value);
        }

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onUploadProgress(event.loaded, event.total);
            }
        };

        const handleAbort = () => xhr.abort();
        signal.addEventListener("abort", handleAbort);
        const cleanup = () => signal.removeEventListener("abort", handleAbort);

        xhr.onload = () => {
            cleanup();
            resolve(
                new Response(nullBodyStatuses.has(xhr.status) ? null : (xhr.response as Blob), {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    headers: parseResponseHeaders(xhr.getAllResponseHeaders()),
                }),
            );
        };
        xhr.onerror = () => {
            cleanup();
            reject(new TypeError("Network request failed"));
        };
        xhr.ontimeout = () => {
            cleanup();
            reject(new TypeError("Network request timed out"));
        };
        xhr.onabort = () => {
            cleanup();
            reject(new DOMException("The request was aborted.", "AbortError"));
        };

        xhr.send((init.body ?? null) as XMLHttpRequestBodyInit | null);
    });
}
